/**
 * React Hook: Create Metadata Retrieval
 * 
 * Creates a new metadata retrieval for an organization that has no existing retrieval.
 * This is called when `latestMetadataRetrieval` is null in the organization data.
 * 
 * Flow:
 * 1. User visits app → GetUserData returns org with latestMetadataRetrieval: null
 * 2. Call this hook's mutation to create a new retrieval
 * 3. Subscribe to progress via useRetrievalProgress with returned retrievalId
 */

import { useMutation } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';

// ============================================================================
// Types
// ============================================================================

export interface CreateRetrievalInput {
  /** Organization ID */
  organizationId: string;
}

export interface CreateRetrievalResponse {
  startMetadataRetrieval: {
    /** The newly created retrieval ID */
    id: string;
    /** Current status (usually 'in_progress') */
    status: string;
    /** Current phase of retrieval */
    currentPhase: string;
  };
}

// ============================================================================
// GraphQL Mutation
// ============================================================================

const START_METADATA_RETRIEVAL_MUTATION = `
  mutation StartRetrieval($input: StartRetrievalInput!) {
    startMetadataRetrieval(input: $input) {
      id
      status
      currentPhase
    }
  }
`;

// ============================================================================
// Hook
// ============================================================================

export function useCreateRetrieval() {

  const mutation = useMutation({
    mutationFn: async (input: CreateRetrievalInput) => {
      const response = await graphqlRequest<CreateRetrievalResponse>(
        START_METADATA_RETRIEVAL_MUTATION,
        { input }
      );
      
      // Handle null response (backend error)
      if (!response.startMetadataRetrieval) {
        throw new Error('Failed to create metadata retrieval - backend returned null');
      }
      
      return response.startMetadataRetrieval;
    },
    onSuccess: (data) => {
      console.log('✅ Metadata retrieval created:', {
        id: data.id,
        status: data.status,
        currentPhase: data.currentPhase,
      });
      console.log('🚀 Retrieval started, retrievalId:', data.id);
    },
    onError: (error) => {
      console.error('❌ Failed to create metadata retrieval:', error);
    },
  });

  return {
    /** Create a new metadata retrieval */
    createRetrieval: mutation.mutateAsync,
    /** Whether the mutation is pending */
    isPending: mutation.isPending,
    /** Error if mutation failed */
    error: mutation.error,
    /** Whether the mutation was successful */
    isSuccess: mutation.isSuccess,
    /** Response data from successful mutation */
    data: mutation.data,
    /** Reset mutation state */
    reset: mutation.reset,
  };
}
